import { badmadRobotQuests } from "@/data-access/quests/badmadRobotQuests";
import { battleOfSoulsQuests } from "@/data-access/quests/battleOfSoulsQuests";
import { battleRiseQuests } from "@/data-access/quests/battleRiseQuests";
import { bitpoolQuests } from "@/data-access/quests/bitpoolQuests";
import { champzQuests } from "@/data-access/quests/champzQuests";
import { chessUniverseQuests } from "@/data-access/quests/chessUniverseQuests";
import { chumbiValleyQuests } from "@/data-access/quests/chumbiValleyQuests";
import { darkMachineQuests } from "@/data-access/quests/darkMachineQuests";
import { echoesOfCryptaliaQuests } from "@/data-access/quests/echoesOfCryptaliaQuests";
import { galaxyOfLemuriaQuests } from "@/data-access/quests/galaxyOfLemuriaQuest";
import { godsUnchainedQuests } from "@/data-access/quests/godsUnchainedQuests";
import { immortalRisingQuests } from "@/data-access/quests/immortalRisingQuests";
import { inMatch3Quests } from "@/data-access/quests/inMatch3Quests";
import { kingArthurQuests } from "@/data-access/quests/kingArthurQuests";
import { marbleXQuestsList } from "@/data-access/quests/marbleXQuests";
import { normaInMetalandQuests } from "@/data-access/quests/normaInMetalandQuests";
import { normaInMetalandQuestsPoints } from "@/data-access/quests/normaInMetalandQuestsPoints";
import { overknightsQuests } from "@/data-access/quests/overknightsQuests";
import { overtakeQuestsList } from "@/data-access/quests/overtakeQuestsList";
import {
  ravenQuestQuestsMain,
  ravenQuestQuestsVariantA,
} from "@/data-access/quests/ravenQuestQuests";
import { selfMadeBillionaireQuests } from "@/data-access/quests/selfMadeBillionaireQuests";
import { somnisQuests } from "@/data-access/quests/somnisQuests";
import { swordsOfBloodQuests } from "@/data-access/quests/swordsOfBloodQuests";
import { tokyoBeastQuests } from "@/data-access/quests/tokyoBeastQuests";
import { treeVerseQuests } from "@/data-access/quests/treeverseQuests";
import { treeverseQuestsPoints } from "@/data-access/quests/treeverseQuestsPoints";
import { zipClashQuests } from "@/data-access/quests/zipClashQuests";
import type { Content } from "@prismicio/client";
import type { QuestGroup } from "@ui-kit/QuestList/types";
import { useMemo } from "react";

const GAMES_WITH_ONBOARDING_QUESTS = [
  "ravenquest",
  "chessuniverse",
  "overtake",
  "badmad-robots",
  "king-arthur-legends-rise",
  "norma-in-metaland",
  "dark-machine",
  "immortal-rising-2",
  "swords-of-blood",
  "marblex",
  "champignons-of-arborethia",
  "champignons-of-arborethia-b",
  "treeverse",
  "echoes-of-cryptalia",
  "gods-unchained",
  "zipclash",
  "somnis-rumble-rush",
  "bitpool",
  "tokyo-beast",
  "battle-of-souls",
  "self-made-billionaire",
  "the-galaxy-of-lemuria",
  "inmatch3",
  "overknights",
  "battlerise",
  "chumbi-valley",
  // add game uids here
];

const useGetGameQuests = (
  uid: string,
  isPoints: boolean,
  templateTier?: Content.GameDocument["data"]["template_tier"],
) => {
  const ravenQuestQuests: QuestGroup[] = useMemo(
    () => [...ravenQuestQuestsMain, ...ravenQuestQuestsVariantA],
    [],
  );

  switch (uid) {
    case "ravenquest":
      return ravenQuestQuests;
    case "chessuniverse":
      return chessUniverseQuests;
    case "overtake":
      return overtakeQuestsList;
    case "badmad-robots":
      return badmadRobotQuests;
    case "king-arthur-legends-rise":
      return kingArthurQuests;
    case "norma-in-metaland":
      return isPoints ? normaInMetalandQuestsPoints : normaInMetalandQuests;
    case "dark-machine":
      return darkMachineQuests;
    case "immortal-rising-2":
      return immortalRisingQuests;
    case "swords-of-blood":
      return swordsOfBloodQuests;
    case "marblex":
      return marbleXQuestsList;
    case "champignons-of-arborethia":
    case "champignons-of-arborethia-b":
      return champzQuests;
    case "treeverse":
      if (isPoints) {
        return treeverseQuestsPoints;
      }
      return treeVerseQuests;
    case "echoes-of-cryptalia":
      return echoesOfCryptaliaQuests;
    case "gods-unchained":
      return godsUnchainedQuests;
    case "zipclash":
      return zipClashQuests;
    case "somnis-rumble-rush":
      return somnisQuests;
    case "bitpool":
      return bitpoolQuests;
    case "tokyo-beast":
      return tokyoBeastQuests;
    case "battle-of-souls":
      return battleOfSoulsQuests;
    case "self-made-billionaire":
      return selfMadeBillionaireQuests;
    case "the-galaxy-of-lemuria":
      return galaxyOfLemuriaQuests;
    case "inmatch3":
      return inMatch3Quests;
    case "overknights":
      return overknightsQuests;
    case "battlerise":
      return battleRiseQuests;
    case "chumbi-valley":
      return chumbiValleyQuests;
    // TODO: add quests for other games
    default:
      return [];
  }
};

export const useOnboardingQuestData = (
  uid: string,
  isPointsTemplate = false,
  templateTier?: Content.GameDocument["data"]["template_tier"],
) => {
  const gameQuests = useGetGameQuests(uid, isPointsTemplate, templateTier);
  const payload = useMemo(
    () => ({
      questsGroups: gameQuests,
      hasOnboardingQuests: GAMES_WITH_ONBOARDING_QUESTS.includes(uid),
    }),
    [uid, gameQuests],
  );
  return payload;
};
